import type { ReactNode } from "react";

/** A labelled field. Owner rule: an empty *required* field is highlighted —
 * ``required`` adds a "*", ``invalid`` (the field is required and empty / wrong)
 * turns the label amber and outlines the input (see ``label[data-invalid]`` in
 * index.css); ``error`` is the short reason shown under it. */
export function Labeled({
  label,
  children,
  required = false,
  invalid = false,
  error,
}: {
  label: ReactNode;
  children: ReactNode;
  required?: boolean;
  invalid?: boolean;
  error?: string;
}) {
  return (
    <label className="block" data-invalid={invalid ? "true" : undefined}>
      <span
        className={
          "nm-label mb-1 block " +
          (invalid ? "text-amber-700" : "text-sand-muted")
        }
      >
        {label}
        {required && <span className="nm-req ml-0.5 text-amber-700"> *</span>}
      </span>
      {children}
      {invalid && error && (
        <span role="alert" className="mt-1 block text-xs text-amber-700">
          {error}
        </span>
      )}
    </label>
  );
}

const inputCls =
  "w-full rounded-md border border-olive-600 bg-olive-900 px-3 py-2 text-sm " +
  "text-sand placeholder:text-sand-muted focus:border-gold focus:outline-none";

export function TextField(
  props: React.InputHTMLAttributes<HTMLInputElement>,
) {
  return <input {...props} className={inputCls + " " + (props.className ?? "")} />;
}

export function TextArea(
  props: React.TextareaHTMLAttributes<HTMLTextAreaElement>,
) {
  return (
    <textarea
      rows={3}
      {...props}
      className={inputCls + " " + (props.className ?? "")}
    />
  );
}

// The date / date-time pickers are a custom brand-styled calendar popup —
// re-exported here so `import { DateField } from "@/components/Form"` keeps
// working across the dashboard.
export { DateField, DateTimeField, DatePicker } from "@/components/DatePicker";

export function Select(
  props: React.SelectHTMLAttributes<HTMLSelectElement>,
) {
  return (
    <select
      {...props}
      className={
        "w-full rounded-md border border-olive-600 bg-olive-900 px-2 py-2 text-sm " +
        (props.className ?? "")
      }
    />
  );
}

/** "a, b , c" <-> ["a","b","c"] */
export const splitList = (s: string) =>
  s.split(",").map((x) => x.trim()).filter(Boolean);
export const joinList = (xs: string[] | null | undefined) => (xs ?? []).join(", ");
